import React, {useCallback, useState} from 'react';
import {Col, Container, Row} from 'react-bootstrap';
import InstallmentsListWidget from '../../widgets/InstallmentsListWidget';
import Card from '../../elements/ReactBootstrap/Card';
import {useParams} from 'react-router-dom';
import {
    useCreatePolicyFromOfferMutation,
    useGetOfferQuery,
    useSendVoucherEmailMutation
} from '../../../features/apiSlice';
import ErrorWidget from '../../widgets/ErrorWidget';
import InstallmentPaymentWidget from '../../widgets/InstallmentPaymentWidget';
import SuccessfulIssueWidget from '../../widgets/SuccessfulIssueWidget';
import PolicyInfoWidget from '../../widgets/Policy/PolicyInfoWidget.jsx';
import Alert from '../../elements/ReactBootstrap/Alert';
import PolicyPrintButton from '../../elements/PrintButtons/PolicyPrintButton';
import VoucherPrintButton from '../../elements/PrintButtons/VoucherPrintButton';
import {printClientGDPR, printPolicy, printPolicyQuestionnaire, printVoucher} from '../../../print';
import Content from '../../partials/Content.jsx';
import VoucherEmailButton from '../../elements/Buttons/VoucherEmailButton.jsx';
import SmallSpinner from '../../elements/Spinner/SmallSpinner.jsx';
import SlowSpinner from '../../elements/Spinner/SlowSpinner.jsx';
import BankAccountInfo from '../../elements/BankAccountInfo.jsx';
import BasicPremiumAmount from "../../elements/Values/BasicPremiumAmount.jsx";
import ClientGDPRPrintButton from "../../elements/PrintButtons/ClientGDPRPrintButton.jsx";
import PolicyQuestionnairePrintButton from "../../elements/PrintButtons/PolicyQuestionnairePrintButton.jsx";
import TravelAssistanceOfferInfoWidget from "../../widgets/TravelAssistance/TravelAssistanceOfferInfoWidget.jsx";

const TravelAssistanceIssue = () => {
    const [policyPrinting, setPolicyPrinting] = useState(false);
    const [voucherPrinting, setVoucherPrinting] = useState(false);
    const [clientGDPRPrinting, setClientGDPRPrinting] = useState(false);
    const [policyQuestionnairePrinting, setPolicyQuestionnairePrinting] = useState(false);

    const printing = policyPrinting || voucherPrinting || clientGDPRPrinting || policyQuestionnairePrinting;

    const [sending, setSending] = useState(false);

    const [selectedInstallments, setSelectedInstallments] = useState([]);

    const {
        offerId,
    } = useParams();

    const {
        data: offer,
        error: findError,
        isSuccess: findSuccess,
        isError: findIsError,
        isLoading: findLoading,
    } = useGetOfferQuery(offerId, {
        skip: offerId === undefined,
    });

    const [issue, {
        data: policy,
        error: issueError,
        isSuccess: issueSuccess,
        isError: issueIsError,
        isLoading: issueLoading,
    }] = useCreatePolicyFromOfferMutation();

    const [send, {
        isSuccess: sendSuccess,
        isError: sendIsError,
        error: sendError,
    }] = useSendVoucherEmailMutation();

    const getFirstInstallment = useCallback(
        installments => installments.filter(installment => installment.number === 1).shift(),
        []
    );

    const getFirstVoucher = useCallback(installment => installment && installment.paidPremiumFromClient && (
        installment.vouchers.filter(voucher => voucher.sticker !== null).shift() ||
        installment.vouchers.concat().shift()
    ), []);


    const offerInstallments = findSuccess ? offer.installments : [];
    const policyInstallments = issueSuccess ? policy.installments : [];
    const installments = issueSuccess ? policyInstallments : offerInstallments;

    const offerFirstInstallment = getFirstInstallment(offerInstallments);
    const policyFirstInstallment = getFirstInstallment(policyInstallments);
    const policyFirstVoucher = getFirstVoucher(policyFirstInstallment);

    const payFirstInstallment = selectedInstallments.includes(offerFirstInstallment);

    return (<Content title="Издаване на нова полица Помощ при пътуване">
        <Row>
            <Col>
                {findIsError && <ErrorWidget error={findError} />}

                {findLoading && <Card>
                    <SmallSpinner />
                </Card>}

                {findSuccess && offer.installments.length > 0 && <Card noPadding>
                    <InstallmentsListWidget product={{
                        hasMtplCover: false,
                        hasPassengersAccidentCover: false,
                    }} installments={installments} disabled={issueLoading}
                        readOnly={issueSuccess} onSelectedInstallmentsChange={setSelectedInstallments}
                    />
                </Card>}

                {offerFirstInstallment && !issueSuccess &&
                <Card>
                    <InstallmentPaymentWidget totalAmount={offerFirstInstallment.totalAmount}
                        showBeginDate={true}
                        showTransportationType={offer.insuranceCompany.number === 8}
                        onSubmit={async values => {
                        const msg = 'Издаване на полица и плащане на вноска на стойност ' +
                            BasicPremiumAmount({
                                amount: offerFirstInstallment.totalAmount,
                                withCurrency: BasicPremiumAmount.defaultProps.withCurrency, // manually calling function
                                currency: offerFirstInstallment.defaultCurrency,
                            }) + ' ?';

                        // TODO change with react-bootstrap modal
                        if (!window.confirm(msg)) {
                            throw new Error('Издаването беше отказано');
                        }

                        let policy;

                        policy = await issue({
                            offer: offer.id,
                            ...values,
                            payFirstInstallment,
                        }).unwrap();

                        setPolicyPrinting(true);
                        await printPolicy(policy.id);
                        setPolicyPrinting(false);

                        const firstInstallment = getFirstInstallment(policy.installments);

                        const firstVoucher = getFirstVoucher(firstInstallment);
                        if (firstVoucher) {
                            setVoucherPrinting(true);
                            await printVoucher(firstVoucher.id);
                            setVoucherPrinting(false);
                        }
                        setClientGDPRPrinting(true);
                        await printClientGDPR(policy.insuredClients[0].insuredClient.id);
                        setClientGDPRPrinting(false);

                        setPolicyQuestionnairePrinting(true);
                        await printPolicyQuestionnaire(policy.id);
                        setPolicyQuestionnairePrinting(false);
                    }} />
                </Card>}

                {issueIsError && <ErrorWidget error={issueError} />}

                {issueLoading && <Alert variant="secondary" icon={null} heading={<>
                    <span className="me-2">
                        <SlowSpinner />
                    </span>
                    Моля, изчакайте...
                </>}>
                    Издаването на полица може да отнеме от 20 секунди до 2 минути.<br/>
                    Моля, не натискайте други бутони и не затваряйте тази страница, докато не приключи!
                </Alert>}

                {issueSuccess && <>
                    <SuccessfulIssueWidget policy={policy} />
                    {policyFirstVoucher.paymentMethod === 'BANK_ACCOUNT_BROKER' && <Card
                        header="Данни за банков превод">
                        <BankAccountInfo
                            totalAmount={policyFirstVoucher.totalAmount}
                            reason={policy.number} />
                    </Card>}
                    <Container as={Card}>
                        <Row className="row-cols-1 row-cols-md-2 row-cols-lg-1 row-cols-xl-2 g-3">
                            <Col>
                                <PolicyPrintButton policy={policy} printing={policyPrinting} disabled={printing}
                                                   setDisabled={setPolicyPrinting} />
                            </Col>
                            <Col>
                                {policyFirstVoucher && <VoucherPrintButton voucher={policyFirstVoucher}
                                   printing={voucherPrinting} disabled={printing} setDisabled={setVoucherPrinting} />}
                            </Col>
                            <Col>
                                <ClientGDPRPrintButton client={policy.insuredClients[0].insuredClient} printing={clientGDPRPrinting}
                                                       disabled={printing} setDisabled={setClientGDPRPrinting}/>
                            </Col>
                            <Col>
                                <PolicyQuestionnairePrintButton policy={policy} printing={policyQuestionnairePrinting}
                                                       disabled={printing} setDisabled={setPolicyQuestionnairePrinting}/>
                            </Col>
                            <Col>
                                <VoucherEmailButton onClick={() => send(policyFirstVoucher.id)} disabled={sending}
                                    setDisabled={setSending} />
                            </Col>
                        </Row>
                    </Container>
                    {sendSuccess && <Alert variant="success" heading="Документите бяха изпратени успешно" />}
                    {sendIsError && <ErrorWidget error={sendError} />}
                </>}
            </Col>
            {findSuccess && <Col>
                {issueSuccess ? <PolicyInfoWidget policy={policy} header="Информация за полицата" /> :
                <Card header="Информация за полицата">
                    <TravelAssistanceOfferInfoWidget />
                </Card>}
            </Col>}
        </Row>
    </Content>);
};

export default TravelAssistanceIssue;
